import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useState } from "react";
import { WithSearch } from "@elastic/react-search-ui";
import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { isEmpty } from "../../utils/nativeUtils";
import { categoryNames } from "../../config";
import { UIStateStoreContext } from "../../stores/uiStateStore";
import { FilterOptionEnum } from "../../models/dataModel";
import { RadioButtonFacet } from "./RadioButtonFacet";
const CategoryFacet = observer(({ labelKey, options, showMore, onChange, onMoreClick, filters, removeFilter, addFilter, clearFilters, hasResolvedCategory, setHasResolvedCategory, }) => {
    const uiStore = useContext(UIStateStoreContext);
    const intl = useIntl();
    const selected = options.find((option) => {
        return option.selected;
    });
    useEffect(() => {
        if (!hasResolvedCategory && !!selected && !uiStore.getOpenCategory()) {
            resolveOpenCategory(selected, removeFilter, addFilter);
            setHasResolvedCategory(true);
        }
    }, [selected, uiStore.getOpenCategory()]);
    function resolveOpenCategory(selected, removeFilter, addFilter) {
        uiStore.setOpenCategory(selected.value);
        removeFilter(FilterOptionEnum.CATEGORY);
        addFilter(FilterOptionEnum.CATEGORY, selected.value);
    }
    function onSelectCategory(value, clearFilters, addFilter) {
        clearFilters();
        if (uiStore.getOpenCategoryFilters(value)) {
            uiStore.getOpenCategoryFilters(value).forEach((filter) => filter.values.forEach((value) => {
                addFilter(filter.field, value, filter.type);
            }));
        }
        uiStore.setOpenCategory(value);
        onChange(value);
    }
    function handleChange(value) {
        uiStore.setOpenCategoryFilters(filters);
        onSelectCategory(value, clearFilters, addFilter);
    }
    function getOptionLabel(option) {
        const value = option.value;
        const categoryKey = uiStore.getCategoryKeyBasedOnName(value);
        return categoryKey
            ? intl.formatMessage({ id: `facets.category.${categoryKey}`, defaultMessage: value })
            : value;
    }
    return (_jsx(RadioButtonFacet, { label: labelKey, options: uiStore.getSortedCategoryOptions(), showMore: showMore, onChange: handleChange, onMoreClick: onMoreClick, getOptionLabel: getOptionLabel }));
});
/**
 * Renders the facet with the WithSearch render props
 * Gets initial category options from global search service list
 */
const FacetWrapper = observer(({ labelKey, options, showMore, onChange, onMoreClick, }) => {
    const uiStore = useContext(UIStateStoreContext);
    const [hasResolvedCategory, setHasResolvedCategory] = useState(false);
    // get details for category facets from options (result count, isSelected)
    useEffect(() => {
        if (!isEmpty(options)) {
            uiStore.setCategoryOptions(options);
        }
    }, [options, uiStore]);
    // on first load, set category facets based on services
    useEffect(() => {
        const serviceOptions = categoryNames.map((value) => ({ value }));
        uiStore.setCategoryOptions(serviceOptions);
    }, []);
    return (_jsx(WithSearch, { mapContextToProps: ({ clearFilters, removeFilter, addFilter, filters }) => ({
            clearFilters,
            removeFilter,
            addFilter,
            filters,
        }), children: (props) => (_jsx(CategoryFacet, { labelKey: labelKey, options: options, showMore: showMore, onChange: onChange, onMoreClick: onMoreClick, filters: props.filters, clearFilters: props.clearFilters, removeFilter: props.removeFilter, addFilter: props.addFilter, hasResolvedCategory: hasResolvedCategory, setHasResolvedCategory: setHasResolvedCategory })) }));
});
/**
 * Renders configured categories with translated labels and canonical filter values.
 * Unsupported options are excluded, and services sharing a category name appear once.
 *
 * @param props Category facet configuration and Search UI callbacks.
 * @param props.label Translation key for the facet title.
 * @param props.options Search UI category options with counts and selection state.
 * @param props.showMore Whether to display the control for additional options.
 * @param props.onChange Receives the canonical category value when selected.
 * @param props.onMoreClick Loads additional options when requested.
 * @returns The category selector and subfacets for the selected category.
 */
export const SelectCategoryFacet = ({ label, options, showMore, onChange, onMoreClick, }) => {
    return (_jsx(FacetWrapper, { labelKey: label, options: options.filter((option) => categoryNames.some((name) => name === option.value)), showMore: showMore, onChange: onChange, onMoreClick: onMoreClick }));
};
